import React, { useState, useRef, useCallback } from 'react';
import type { PageDetailOut } from '../types/catalog';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Mic, Square, Play, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { startRecording, blobToUrl } from '../utils/mediaRecorder';

interface SpeakingItem {
  id: string;
  word?: string;
  expected_text?: string;
  accepted?: string[];
  image?: { url?: string; alt?: string };
}

interface Section {
  type?: string;
  text?: string;
  heading?: string;
  paragraph?: string;
  items?: SpeakingItem[];
  [key: string]: unknown;
}

interface ContentShape {
  sections?: Section[];
  speech?: { recording?: { max_seconds?: number } };
  validation?: { mode?: string; pass_threshold?: number };
  ui_hints?: { columns?: number; attempts_allowed?: number; show_word_label?: boolean };
}

const STORAGE_PREFIX = 'tkm_speaking';

function getStorageKey(studentId: string, pageId: string, itemId: string): string {
  return `${STORAGE_PREFIX}:${studentId}:${pageId}:${itemId}`;
}

function loadStoredResult(
  studentId: string,
  pageId: string,
  itemId: string
): { attempts: number; last_result?: string; typed_text?: string; recorded_ms?: number } | null {
  try {
    const raw = localStorage.getItem(getStorageKey(studentId, pageId, itemId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      attempts: typeof parsed.attempts === 'number' ? parsed.attempts : 0,
      last_result: parsed.last_result,
      typed_text: parsed.typed_text,
      recorded_ms: parsed.recorded_ms,
    };
  } catch {
    return null;
  }
}

function saveResult(
  studentId: string,
  pageId: string,
  itemId: string,
  payload: {
    attempts: number;
    last_result: 'pass' | 'fail';
    typed_text?: string;
    recorded_ms?: number;
  }
): void {
  try {
    localStorage.setItem(
      getStorageKey(studentId, pageId, itemId),
      JSON.stringify({ ...payload, recorded_ms: payload.recorded_ms ?? Date.now() })
    );
  } catch {
    // ignore
  }
}

function normalizeForMatch(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function matchesAccepted(typed: string, accepted: string[]): boolean {
  const n = normalizeForMatch(typed);
  return accepted.some((a) => normalizeForMatch(a) === n);
}

interface PracticeSpeakingValidateV1Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

export const PracticeSpeakingValidateV1: React.FC<PracticeSpeakingValidateV1Props> = ({
  page,
  onComplete,
}) => {
  const { user } = useAuth();
  const studentId = user?.id ?? 'demo-student';
  const content = (page.page_json_v1?.content ?? {}) as ContentShape;
  const sections = content.sections ?? [];
  const heading = sections.find((s) => (s.type ?? '').toLowerCase() === 'heading');
  const paragraph = sections.find((s) => (s.type ?? '').toLowerCase() === 'paragraph');
  const sectionWithItems = sections.find((s) => Array.isArray(s.items) && s.items.length > 0);
  const items: SpeakingItem[] = sectionWithItems?.items ?? [];
  const maxSeconds = content.speech?.recording?.max_seconds ?? 30;
  const attemptsAllowed = content.ui_hints?.attempts_allowed ?? 3;
  const columns = content.ui_hints?.columns ?? 2;
  const showWordLabel = content.ui_hints?.show_word_label !== false;

  const [recordingItemId, setRecordingItemId] = useState<string | null>(null);
  const [blobs, setBlobs] = useState<Record<string, Blob>>({});
  const [results, setResults] = useState<Record<string, { result: 'pass' | 'fail'; attempts: number }>>({});
  const [modalItemId, setModalItemId] = useState<string | null>(null);
  const [typedText, setTypedText] = useState('');
  const recordingRef = useRef<{ stop: () => void; blobPromise: Promise<Blob> } | null>(null);

  const getText = (s: Section) => (s.text ?? s.heading ?? s.paragraph ?? '') as string;

  const startRecord = useCallback(
    (itemId: string) => {
      if (recordingItemId) return;
      const { stop, blobPromise } = startRecording(maxSeconds);
      recordingRef.current = { stop, blobPromise };
      setRecordingItemId(itemId);
      blobPromise.then((blob) => {
        setBlobs((prev) => ({ ...prev, [itemId]: blob }));
        setRecordingItemId(null);
        recordingRef.current = null;
      });
    },
    [maxSeconds, recordingItemId]
  );

  const stopRecord = useCallback(() => {
    recordingRef.current?.stop();
  }, []);

  const playRecorded = useCallback((itemId: string) => {
    const blob = blobs[itemId];
    if (!blob) return;
    const url = blobToUrl(blob);
    const a = new Audio(url);
    a.onended = () => URL.revokeObjectURL(url);
    a.play();
  }, [blobs]);

  const openSubmitModal = useCallback((itemId: string) => {
    setModalItemId(itemId);
    setTypedText('');
  }, []);

  const handleSubmitValidation = useCallback(() => {
    const itemId = modalItemId;
    if (!itemId || !page.id) return;
    const item = items.find((i) => i.id === itemId);
    const accepted = item?.accepted ?? (item?.expected_text ? [item.expected_text] : []);
    const stored = loadStoredResult(studentId, page.id, itemId);
    const attemptsUsed = (stored?.attempts ?? 0) + 1;
    const passed = typedText.trim() !== '' && matchesAccepted(typedText, accepted);

    saveResult(studentId, page.id, itemId, {
      attempts: attemptsUsed,
      last_result: passed ? 'pass' : 'fail',
      typed_text: typedText,
    });
    setResults((prev) => ({
      ...prev,
      [itemId]: { result: passed ? 'pass' : 'fail', attempts: attemptsUsed },
    }));
    setModalItemId(null);
    setTypedText('');
    if (passed) onComplete?.();
  }, [modalItemId, page.id, items, studentId, typedText, onComplete]);

  if (items.length === 0) {
    return (
      <div className="p-6 text-gray-500 dark:text-gray-400">
        No speaking items in this page.
      </div>
    );
  }

  const gridCols = columns === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2';

  return (
    <div className="space-y-4 p-6 overflow-auto">
      {heading && (
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          {getText(heading)}
        </h1>
      )}
      {paragraph && (
        <p className="text-gray-700 dark:text-gray-300">{getText(paragraph)}</p>
      )}

      <div className={`grid ${gridCols} gap-4`}>
        {items.map((item) => {
          const stored = loadStoredResult(studentId, page.id ?? '', item.id);
          const attemptsUsed = results[item.id]?.attempts ?? stored?.attempts ?? 0;
          const result = results[item.id]?.result ?? stored?.last_result;
          const hasRecording = !!blobs[item.id];
          const isRecording = recordingItemId === item.id;
          const canSubmit = hasRecording && attemptsUsed < attemptsAllowed && result !== 'pass';

          return (
            <div
              key={item.id}
              className="rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex flex-col items-center gap-3"
            >
              {item.image?.url && (
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                  <ImageWithFallback
                    src={item.image.url}
                    alt={item.image.alt ?? item.word ?? ''}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              {!item.image?.url && (
                <div className="w-full aspect-video rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-400 dark:text-gray-500">
                  No image
                </div>
              )}
              {showWordLabel && item.word && (
                <p className="font-medium text-gray-900 dark:text-white">{item.word}</p>
              )}
              <div className="flex flex-wrap gap-2 justify-center">
                {isRecording ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={stopRecord}
                    className="gap-2 text-red-600 border-red-500"
                  >
                    <Square className="w-4 h-4" />
                    Stop
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startRecord(item.id)}
                    className="gap-2"
                  >
                    <Mic className="w-4 h-4" />
                    Record
                  </Button>
                )}
                {hasRecording && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => playRecorded(item.id)}
                    className="gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Replay
                  </Button>
                )}
                {canSubmit && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => openSubmitModal(item.id)}
                  >
                    Submit
                  </Button>
                )}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Attempts: {attemptsUsed} / {attemptsAllowed}
              </div>
              {result === 'pass' && (
                <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  Pass
                </span>
              )}
              {result === 'fail' && attemptsUsed >= attemptsAllowed && (
                <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                  <XCircle className="w-4 h-4" />
                  Try again
                </span>
              )}
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={Boolean(modalItemId)}
        onClose={() => { setModalItemId(null); setTypedText(''); }}
        title="Check your pronunciation"
        size="sm"
      >
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Type what you said so we can check it against the expected text. Voice-only validation will be available in a future update.
          </p>
          <input
            type="text"
            value={typedText}
            onChange={(e) => setTypedText(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            placeholder="Type here..."
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setModalItemId(null); setTypedText(''); }}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSubmitValidation}>
              Submit
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
